import { useEffect, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";

/**
 * OfflineBanner — Iter174.
 *
 * PWA-friendly connectivity indicator. Two states:
 *   • offline  — persistent red banner pinned below the mobile header
 *     (or top of screen on desktop) telling the user their actions
 *     may not save.
 *   • back-online — a brief 3-second emerald confirmation so the user
 *     knows connectivity restored (helps trust when they retry a save).
 *
 * Uses the browser `online`/`offline` events which fire on both iOS
 * Safari and Android Chrome. We treat `navigator.onLine === true` as
 * initial "online" (accurate on both platforms for cellular / wifi).
 *
 * Safe-area aware — sits under the notch via env(safe-area-inset-top),
 * so it doesn't collide with the iPhone status bar.
 */
export default function OfflineBanner() {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [showRecovered, setShowRecovered] = useState(false);

  useEffect(() => {
    const goOffline = () => setOnline(false);
    const goOnline = () => {
      setOnline(true);
      setShowRecovered(true);
      window.setTimeout(() => setShowRecovered(false), 3000);
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (online && !showRecovered) return null;

  const bannerStyle = {
    // Pin below the notch on iOS. On desktop this evaluates to 0.
    top: "env(safe-area-inset-top)",
  };

  if (!online) {
    return (
      <div
        style={bannerStyle}
        data-testid="offline-banner"
        role="status"
        aria-live="polite"
        className="fixed left-0 right-0 z-[60] lg:top-0 flex items-center justify-center gap-2 bg-rose-600/95 text-white text-xs font-medium px-4 py-2 shadow-lg backdrop-blur-sm"
      >
        <WifiOff className="w-3.5 h-3.5" />
        <span>You&apos;re offline — new EOD entries will save once you&apos;re back online.</span>
      </div>
    );
  }

  return (
    <div
      style={bannerStyle}
      data-testid="online-banner"
      role="status"
      aria-live="polite"
      className="fixed left-0 right-0 z-[60] lg:top-0 flex items-center justify-center gap-2 bg-emerald-600/95 text-white text-xs font-medium px-4 py-2 shadow-lg backdrop-blur-sm animate-in slide-in-from-top duration-300"
    >
      <Wifi className="w-3.5 h-3.5" />
      <span>Back online.</span>
    </div>
  );
}
